import { Check, Award, Globe, BadgeCheck, Sparkles } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { PageCTA } from "../components/PageCTA";
import { EuipoTrademark } from "../components/EuipoTrademark";
import { Reveal, Overline } from "../components/Reveal";
import { useLang } from "../context/LanguageContext";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/aac30769-f491-4e91-84b6-42abbea5b108/images/480ed47abb8837de4d45868c027f49c82d0159dab0458873a2209c646b6a7ef8.jpeg";
const SEAL_IMG = "https://images.unsplash.com/photo-1641477176034-1a3e10c343a8";
const G_ICONS = [Award, Globe, BadgeCheck, Sparkles];

export default function About() {
  const { t } = useLang();
  const a = t.pages.about;

  return (
    <div data-testid="about-page">
      <PageHero overline={a.overline} title={a.title} subtitle={a.subtitle} image={HERO_IMG} />

      <section className="relative z-10 py-20 md:py-28 bg-[#050505]">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <p className="font-display text-2xl md:text-3xl leading-snug text-white/85">{a.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="relative z-10 py-20 md:py-28 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Overline>{a.overline}</Overline>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight">{a.missionTitle}</h2>
            <p className="text-white/60 text-lg leading-relaxed mt-8">{a.missionText}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="space-y-px bg-white/10">
              {a.values.map((v, i) => (
                <div key={i} className="bg-[#0a0a0a] p-7 hover:bg-[#111] transition-colors duration-300">
                  <h3 className="font-display text-2xl text-[#D4AF37] mb-2">{v.title}</h3>
                  <p className="text-white/55">{v.text}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative z-10 py-20 md:py-28 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border border-[#D4AF37]/30" />
              <img src={SEAL_IMG} alt="" className="relative w-full h-[380px] md:h-[460px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight">{a.brandTitle}</h2>
            <p className="text-white/60 text-lg leading-relaxed mt-6">{a.brandText}</p>
            <p className="text-white/80 mt-8 mb-4">{a.criteriaTitle}</p>
            <ul className="space-y-4">
              {a.criteria.map((c, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="mt-1 w-5 h-5 shrink-0 flex items-center justify-center bg-[#D4AF37] text-black">
                    <Check size={13} strokeWidth={3} />
                  </span>
                  <span className="text-white/80">{c}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="relative z-10 py-20 md:py-28 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight">{a.guaranteesTitle}</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-14">
            {a.guarantees.map((g, i) => {
              const Icon = G_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.1}>
                  <div className="h-full border border-white/10 hover:border-[#D4AF37]/50 transition-colors duration-300 p-7">
                    <Icon size={24} className="text-[#D4AF37] mb-5" strokeWidth={1.5} />
                    <h3 className="font-display text-xl mb-3">{g.title}</h3>
                    <p className="text-white/55 text-sm leading-relaxed">{g.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <EuipoTrademark />

      <PageCTA label={t.pages.common.partnership} />
    </div>
  );
}
